import Basket from "../../components/organisms/Basket"
import {Header} from "../../components/organisms/Header"
import {Topbar} from "../../components/organisms/Topbar"
import ApiService from "../../core/service/ApiService"
import {initPdiComponent, LOCAL_STORAGE_TUNNEL_DATA_KEY, sendToLogServer} from "../../core/service/BusinessLogicService"
import {loc} from "../../core/utils"
import React, {useEffect, useMemo, useRef} from "react"
import {Col, Container, Row, Spinner} from "react-bootstrap"
import "./styles.css"

const DEFAULT_TIMEOUT_LIMIT_SECONDS = 30
const DEFAULT_POLLING_INTERVAL_SECONDS = 2

const status = Object.freeze({
  SUCCESS: "SUCCESS",
  PENDING: "PENDING",
  REFUSED: "REFUSED",
  CANCELLED: "CANCELLED"
})

export default function AdyenPaymentValidationPage({ tunnelData, history, onSetStep }) {
  const opl = tunnelData?.opl
  const tunnelDataRef = useRef(tunnelData)
  const historyRef = useRef(history)
  const onSetStepRef = useRef(onSetStep)
  const stepIndex = useMemo(
    () => tunnelData?.breadcrumbs?.findIndex((s) => s?.id === tunnelData?.step),
    [tunnelData]
  )
  const previousStep = stepIndex > 0 ? tunnelData?.breadcrumbs?.[stepIndex - 1] : null
  const previousStepRef = useRef(previousStep)

  const isTerminatedRef = useRef(false)
  const hasExecutedTerminalActionRef = useRef(false)
  const pollInFlightRef = useRef(false)
  const runIdRef = useRef(0)

  const intervalIdRef = useRef(null)
  const timeoutIdRef = useRef(null)

  useEffect(() => {
    tunnelDataRef.current = tunnelData
  }, [tunnelData])

  useEffect(() => {
    historyRef.current = history
  }, [history])

  useEffect(() => {
    onSetStepRef.current = onSetStep
  }, [onSetStep])

  useEffect(() => {
    previousStepRef.current = previousStep
  }, [previousStep])

  useEffect(() => {
    initPdiComponent("AdyenPaymentValidationPage")
  }, [])

  useEffect(() => {
    if (!opl) {
      sendToLogServer("ERROR", "AdyenPaymentValidationPage: missing opl in tunnelData")
      return
    }

    // New run (also protects StrictMode double-effect in dev)
    runIdRef.current += 1
    const runId = runIdRef.current
    let cancelled = false
    const isStaleRun = () => cancelled || runId !== runIdRef.current
    const shouldBail = () => isStaleRun() || isTerminatedRef.current || hasExecutedTerminalActionRef.current

    const stopTimers = () => {
      if (intervalIdRef.current) clearInterval(intervalIdRef.current)
      if (timeoutIdRef.current) clearTimeout(timeoutIdRef.current)
      intervalIdRef.current = null
      timeoutIdRef.current = null
    }

    // Reset guards when we (re)enter the page with a new OPL
    isTerminatedRef.current = false
    hasExecutedTerminalActionRef.current = false
    pollInFlightRef.current = false

    const headers = { Authorization: `Bearer ${tunnelData?.access_token}`, Accept: "*/*" }
    const url = tunnelData?.apiUrl + `middle-bank-service/api/v2/payment/adyen/validation/${opl}/${tunnelData.data.order.id}`

    const goToPreviousStep = () => {
      if (hasExecutedTerminalActionRef.current) return
      hasExecutedTerminalActionRef.current = true
      const prev = previousStepRef.current
      if (!prev) return
      const nextTunnelData = { ...tunnelDataRef.current, step: prev.id }
      window.localStorage.setItem(LOCAL_STORAGE_TUNNEL_DATA_KEY, JSON.stringify(nextTunnelData))
      historyRef.current.push(prev.route)
    }

    const handleDto = (dto) => {
      if (isTerminatedRef.current || hasExecutedTerminalActionRef.current) {
        return { shouldStop: true }
      }
      const dtoStatus = dto?.status

      if (dtoStatus === status.SUCCESS) {
        isTerminatedRef.current = true
        stopTimers()
        if (!hasExecutedTerminalActionRef.current) {
          hasExecutedTerminalActionRef.current = true
          onSetStepRef.current()
        }
        return { shouldStop: true }
      }

      if (dtoStatus === status.PENDING) {
        return { shouldStop: false }
      }

      if (dtoStatus === status.REFUSED || dtoStatus === status.CANCELLED) {
        isTerminatedRef.current = true
        stopTimers()
        goToPreviousStep()
        return { shouldStop: true }
      }

      return { shouldStop: false }
    }

    const pollOnce = async () => {
      if (isStaleRun()) return null
      if (pollInFlightRef.current) return null
      pollInFlightRef.current = true
      try {
        return await ApiService.getForEvollisApi(url, headers)
      } catch (err) {
        sendToLogServer("ERROR", `AdyenPaymentValidationPage polling error for opl [${opl}] : ${JSON.stringify(err)}`)
        return null
      } finally {
        pollInFlightRef.current = false
      }
    }

    (async () => {
      const firstDto = await pollOnce()
      if (shouldBail()) return
      const firstResult = handleDto(firstDto)

      // Si la première réponse est déjà terminale (SUCCESS / REFUSED / CANCELLED), on ne démarre pas le polling/timeout.
      if (firstResult?.shouldStop) {
        return
      }

      timeoutIdRef.current = setTimeout(() => {
        if (isStaleRun()) return
        if (isTerminatedRef.current) return
        stopTimers()
        sendToLogServer("INFO", `AdyenPaymentValidationPage TIMEOUT for opl [${opl}]`)
        goToPreviousStep()
      }, DEFAULT_TIMEOUT_LIMIT_SECONDS * 1000)

      intervalIdRef.current = setInterval(async () => {
        if (isStaleRun()) return
        if (isTerminatedRef.current) return
        const dto = await pollOnce()
        if (shouldBail()) return
        const result = handleDto(dto)
        if (result?.shouldStop) {
          stopTimers()
        }
      }, DEFAULT_POLLING_INTERVAL_SECONDS * 1000)
    })()

    return () => {
      cancelled = true
      stopTimers()
    }
  }, [opl, tunnelData])

  return (
    <Container fluid className="white-background">
      <Row noGutters>
        <Col className="main-block" lg={8}>
          <Row>
            <Topbar tunnelData={tunnelData} history={history} />
            <Header tunnelData={tunnelData} />
            <Col sm={12}>
              <Container className="adyen-validation">
                <Row className="justify-content-center">
                  <Col xs={12} sm={10} md={8} lg={7} xl={6}>
                    <div className="adyen-validation__content text-center">
                      <div
                        className="adyen-validation__spinner"
                        style={{ "--brand-color": tunnelData?.brandColor || "#0d6efd" }}
                        aria-hidden="true"
                      >
                        <Spinner animation="border" role="status" />
                      </div>

                      <h1 className="adyen-validation__title">{loc("Payment validation")}</h1>
                      <p className="adyen-validation__subtitle">{loc("Validation de votre paiement en cours")}</p>
                    </div>
                  </Col>
                </Row>
              </Container>
            </Col>
          </Row>
        </Col>
        <Col className="aside-block d-none d-lg-block d-xl-block" lg={4}>
          <Basket tunnelData={tunnelData} />
        </Col>
      </Row>
    </Container>
  )
}

